// src/Components/EmployeeForm.js
import { memo, useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import api from '../api/api.js';
import { makeEmployeeSchema, getPasswordStrength } from '../lib/validations';
import Button from './ui/Button.jsx';
import PasswordInput from './ui/PasswordInput.jsx';
import InfoBox from './ui/InfoBox.jsx';
import { useToast } from './ui/Toast.jsx';
import f from '../styles/forms.module.css';

const blockNonLetters = (e) => {
  if (e.key.length === 1 && !/[a-zA-ZÀ-ÿ\s]/.test(e.key)) e.preventDefault();
};

const blockNonDigits = (e) => {
  const nav = ['Backspace', 'Delete', 'Tab', 'ArrowLeft', 'ArrowRight', 'Home', 'End'];
  if (!nav.includes(e.key) && !/^[0-9]$/.test(e.key)) e.preventDefault();
};

const EmployeeForm = memo(({ form, resetForm, onSuccess }) => {
  const isEditing = !!form.id;
  const { showToast } = useToast();
  const schema = useMemo(() => makeEmployeeSchema(isEditing), [isEditing]);

  const {
    register,
    handleSubmit,
    reset,
    watch,
    formState: { errors, isSubmitting },
  } = useForm({
    resolver: zodResolver(schema),
    mode: 'onBlur',
    reValidateMode: 'onChange',
    defaultValues: {
      name:           form.name           || '',
      last_name:      form.last_name      || '',
      identification: form.identification || '',
      birthdate:      form.birthdate      || '',
      mail:           form.mail           || '',
      tel_number:     form.tel_number     || '',
      position:       form.position       || '',
      password:       '',
      oldPassword:    '',
    },
  });

  useEffect(() => {
    reset({
      name:           form.name           || '',
      last_name:      form.last_name      || '',
      identification: form.identification || '',
      birthdate:      form.birthdate      || '',
      mail:           form.mail           || '',
      tel_number:     form.tel_number     || '',
      position:       form.position       || '',
      password:       '',
      oldPassword:    '',
    });
  }, [form.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const [serverError, setServerError] = useState('');

  // Para la barra de fortaleza de contraseña
  const passwordValue = watch('password') || '';
  const strength = getPasswordStrength(passwordValue);

  const onSubmit = async (data) => {
    setServerError('');
    try {
      const email = isEditing ? form.mail : (data.mail || '').toLowerCase().trim();
      const payload = {
        Name:           data.name.trim(),
        Last_name:      data.last_name.trim(),
        Email:          email,
        Number_phone:   data.tel_number,
        Identification: data.identification.trim(),
        Birthdate:      data.birthdate,
        Rol:            'employee',
        Position:       data.position,
      };

      if (data.password) {
        payload.password = data.password;
        if (isEditing) payload.oldPassword = data.oldPassword;
      }

      if (isEditing) {
        await api.put(`/user/${form.id}`, payload);
        onSuccess({ ...form, ...payload, id: form.id });
      } else {
        const res = await api.post('/user', payload);
        const empId = res.data.userId || res.data.id;
        if (!empId) throw new Error('El servidor no devolvió el ID del empleado creado');
        onSuccess({ id: empId, name: data.name, last_name: data.last_name, mail: data.mail, position: data.position });
      }

      showToast(`Empleado ${isEditing ? 'actualizado' : 'creado'} correctamente`, { type: 'success' });
      resetForm();
    } catch (err) {
      setServerError(
        err.response?.data?.errors?.[0]?.message ||
        err.response?.data?.error ||
        err.message ||
        'Hubo un error al guardar el empleado.'
      );
    }
  };

  return (
    <div className={f.card}>
      {/* Header */}
      <div className={f.cardHeader}>
        <div className={f.cardTitleGroup}>
          <h3 className={f.cardTitle}>{isEditing ? 'Editar Empleado' : 'Nuevo Empleado'}</h3>
          <p className={f.cardDesc}>
            {isEditing
              ? 'Modifica los datos del empleado. Deja la contraseña vacía para no cambiarla.'
              : 'Registra un nuevo empleado. Se le enviará acceso al sistema.'}
          </p>
        </div>
        <span className={f.badgeEmployee}>Empleado</span>
      </div>
      <hr className={f.divider} />

      <form onSubmit={handleSubmit(onSubmit)} noValidate>
        {serverError && <div className={f.serverError} role="alert">{serverError}</div>}

        {/* ── Información Personal ── */}
        <div className={f.section}>
          <span className={f.sectionLabel}>Información Personal</span>
          <div className={f.grid2}>
            <div className={f.field}>
              <label htmlFor="emp-name" className={`${f.label} ${f.required}`}>Nombre</label>
              <input
                id="emp-name"
                placeholder="Ej: María"
                className={`${f.input} ${errors.name ? f.inputError : ''}`}
                aria-describedby={errors.name ? 'emp-name-error' : undefined}
                aria-invalid={!!errors.name}
                onKeyDown={blockNonLetters}
                {...register('name')}
              />
              {errors.name && <span id="emp-name-error" className={f.fieldError} role="alert">{errors.name.message}</span>}
            </div>

            <div className={f.field}>
              <label htmlFor="emp-lastname" className={`${f.label} ${f.required}`}>Apellido</label>
              <input
                id="emp-lastname"
                placeholder="Ej: González"
                className={`${f.input} ${errors.last_name ? f.inputError : ''}`}
                aria-describedby={errors.last_name ? 'emp-lastname-error' : undefined}
                aria-invalid={!!errors.last_name}
                onKeyDown={blockNonLetters}
                {...register('last_name')}
              />
              {errors.last_name && <span id="emp-lastname-error" className={f.fieldError} role="alert">{errors.last_name.message}</span>}
            </div>

            <div className={f.field}>
              <label htmlFor="emp-id" className={`${f.label} ${f.required}`}>Identificación</label>
              <input
                id="emp-id"
                placeholder="Solo números"
                inputMode="numeric"
                className={`${f.input} ${errors.identification ? f.inputError : ''}`}
                aria-describedby={errors.identification ? 'emp-id-error' : undefined}
                aria-invalid={!!errors.identification}
                onKeyDown={blockNonDigits}
                {...register('identification')}
              />
              {errors.identification && <span id="emp-id-error" className={f.fieldError} role="alert">{errors.identification.message}</span>}
            </div>

            <div className={f.field}>
              <label htmlFor="emp-birth" className={`${f.label} ${f.required}`}>Fecha de Nacimiento</label>
              <input
                id="emp-birth"
                type="date"
                max={(() => { const d = new Date(); d.setFullYear(d.getFullYear() - 18); return d.toISOString().split('T')[0]; })()}
                className={`${f.input} ${errors.birthdate ? f.inputError : ''}`}
                aria-describedby={errors.birthdate ? 'emp-birth-error' : 'emp-birth-hint'}
                aria-invalid={!!errors.birthdate}
                {...register('birthdate')}
              />
              {errors.birthdate
                ? <span id="emp-birth-error" className={f.fieldError} role="alert">{errors.birthdate.message}</span>
                : <span id="emp-birth-hint" className={f.fieldHint}>El empleado debe ser mayor de 18 años</span>
              }
            </div>
          </div>
        </div>

        {/* ── Contacto ── */}
        <div className={f.section}>
          <span className={f.sectionLabel}>Información de Contacto</span>
          <div className={f.grid2}>
            <div className={f.field}>
              <label htmlFor="emp-mail" className={`${f.label} ${f.required}`}>Correo Electrónico</label>
              <input
                id="emp-mail"
                type="email"
                placeholder="empleado@flamingo.com"
                className={`${f.input} ${errors.mail ? f.inputError : ''}`}
                aria-describedby={errors.mail ? 'emp-mail-error' : undefined}
                aria-invalid={!!errors.mail}
                disabled={isEditing}
                {...register('mail')}
              />
              {isEditing && <span className={f.fieldHint}>El correo no puede modificarse</span>}
              {errors.mail && !isEditing && <span id="emp-mail-error" className={f.fieldError} role="alert">{errors.mail.message}</span>}
            </div>

            <div className={f.field}>
              <label htmlFor="emp-phone" className={`${f.label} ${f.required}`}>Teléfono</label>
              <input
                id="emp-phone"
                placeholder="Solo dígitos"
                inputMode="numeric"
                className={`${f.input} ${errors.tel_number ? f.inputError : ''}`}
                aria-describedby={errors.tel_number ? 'emp-phone-error' : undefined}
                aria-invalid={!!errors.tel_number}
                onKeyDown={blockNonDigits}
                {...register('tel_number')}
              />
              {errors.tel_number && <span id="emp-phone-error" className={f.fieldError} role="alert">{errors.tel_number.message}</span>}
            </div>
          </div>
        </div>

        {/* ── Cargo ── */}
        <div className={f.section}>
          <span className={f.sectionLabel}>Cargo</span>
          <div className={f.field} style={{ maxWidth: '260px' }}>
            <label htmlFor="emp-position" className={`${f.label} ${f.required}`}>Posición</label>
            <select
              id="emp-position"
              className={`${f.select} ${errors.position ? f.inputError : ''}`}
              aria-describedby={errors.position ? 'emp-pos-error' : undefined}
              aria-invalid={!!errors.position}
              {...register('position')}
            >
              <option value="">Seleccione un cargo</option>
              <option value="conductor">Conductor</option>
              <option value="vendedor">Vendedor</option>
              <option value="administrador">Administrador</option>
            </select>
            {errors.position && <span id="emp-pos-error" className={f.fieldError} role="alert">{errors.position.message}</span>}
          </div>
        </div>

        {/* ── Acceso / Contraseña ── */}
        <div className={f.section}>
          <span className={f.sectionLabel}>{isEditing ? 'Cambio de Contraseña' : 'Acceso al Sistema'}</span>

          {isEditing && (
            <InfoBox>
              Deja estos campos vacíos si no deseas cambiar la contraseña del empleado.
            </InfoBox>
          )}

          <div className={f.grid2} style={{ marginTop: isEditing ? '12px' : '0' }}>
            {isEditing && (
              <div className={f.field}>
                <label htmlFor="emp-old-pwd" className={f.label}>Contraseña Actual</label>
                <PasswordInput
                  id="emp-old-pwd"
                  placeholder="Contraseña actual"
                  error={!!errors.oldPassword}
                  aria-describedby={errors.oldPassword ? 'emp-old-pwd-error' : undefined}
                  {...register('oldPassword')}
                />
                {errors.oldPassword && <span id="emp-old-pwd-error" className={f.fieldError} role="alert">{errors.oldPassword.message}</span>}
              </div>
            )}

            <div className={f.field}>
              <label htmlFor="emp-pwd" className={`${f.label} ${!isEditing ? f.required : ''}`}>
                {isEditing ? 'Nueva Contraseña' : 'Contraseña'}
              </label>
              <PasswordInput
                id="emp-pwd"
                placeholder={isEditing ? 'Nueva contraseña (opcional)' : 'Mínimo 8 caracteres'}
                error={!!errors.password}
                showStrength
                strengthValue={strength}
                aria-describedby={errors.password ? 'emp-pwd-error' : undefined}
                {...register('password')}
              />
              {errors.password && <span id="emp-pwd-error" className={f.fieldError} role="alert">{errors.password.message}</span>}
            </div>
          </div>
        </div>

        {/* ── Acciones ── */}
        <div className={f.actions}>
          <Button type="submit" variant="primary" loading={isSubmitting}>
            {isEditing ? 'Actualizar Empleado' : 'Registrar Empleado'}
          </Button>
          {isEditing && (
            <Button type="button" variant="secondary" onClick={resetForm}>
              Cancelar
            </Button>
          )}
        </div>
      </form>
    </div>
  );
});

export default EmployeeForm;
